import { defineStore } from 'pinia';

export type IFileTransaction = {
    id: string;
    date: number;
    name: string;
    systemSectionLabel?: string;
    direction: 'upload' | 'download';
    progress: number | null;
    total: number;
    isFinished: boolean;
    error?: { message: string };
    cancel: () => void;
    download?: () => void;
};
/**
 * Store for managing file transactions.
 * @returns {Object} The file transactions store.
 */
export const useFileTransactionsStore = defineStore('fileTransactions', {
    state: () => ({
        transactions: [] as IFileTransaction[],
    }),
    getters: {
        uploads: (state) => state.transactions.filter((t) => t.direction === 'upload'),
        downloads: (state) => state.transactions.filter((t) => t.direction === 'download'),
        totalProgress: (state) =>
            state.transactions.reduce(
                (acc, cur) => {
                    if (cur.isFinished || cur.error) return acc;
                    const total = acc.total + cur.total;
                    const progress = acc.progress + (cur.progress ?? 0);
                    return { total, progress, percent: Math.round((progress * 100) / total) };
                },
                { total: 0, progress: 0, percent: 0 }
            ),
        areAllFinished: (state) => state.transactions.every((tr) => tr.isFinished),
    },
    actions: {
        push(transaction: IFileTransaction) {
            this.transactions.push(transaction);
        },
        update(transaction: Pick<IFileTransaction, 'id'> & Partial<IFileTransaction>) {
            const tIndex = this.transactions.findIndex((tr) => tr.id === transaction.id);
            if (tIndex === -1) {
                console.warn(`Cannot find file transaction with id ${transaction.id}`, transaction);
                return;
            }
            this.transactions[tIndex] = { ...this.transactions[tIndex], ...transaction };
        },
        remove(id: string) {
            this.transactions = this.transactions.filter((t) => t.id !== id);
        },
    },
});
